import { useMemo, memo } from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Scatter,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { getDateAggregationKey } from '../../../utils/getDateAggregationKey';
import { deriveQuantArcBacktestSeries, type QuantArcBacktestRow } from './backtestSeries';
import { WidgetWrapper } from './WidgetWrapper';
import { downsampleWithMarkers } from '../../../utils/performance';

interface Props {
    data: QuantArcBacktestRow[];
    chartData?: Array<Record<string, string | number | null>> | null;
    chartSeriesLabels?: Record<string, string> | null;
}

function formatNumber(value: unknown, digits = 2): string {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return numeric.toFixed(digits);
}

function toFiniteNumber(value: unknown): number | null {
    if (value == null) return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

function mergeExecutionSeries(
    rows: QuantArcBacktestRow[],
    chartData?: Array<Record<string, string | number | null>> | null,
) {
    const eventSeries = deriveQuantArcBacktestSeries(rows);
    if (!chartData?.length) {
        return eventSeries;
    }

    const mergedByTime = new Map<string, any>();
    for (const row of chartData) {
        const time = typeof row.time === 'string' ? row.time : null;
        if (!time) continue;
        mergedByTime.set(time, {
            time,
            entry_reference_price: toFiniteNumber(row.entry_reference_price),
            exit_reference_price: toFiniteNumber(row.exit_reference_price),
        });
    }

    for (const point of eventSeries) {
        const existing = mergedByTime.get(point.time) ?? { time: point.time };
        mergedByTime.set(point.time, {
            ...existing,
            ...point,
        });
    }

    const merged = Array.from(mergedByTime.values()).sort(
        (a, b) => a.time.localeCompare(b.time)
    );

    let lastPosition = 0;
    for (const point of merged) {
        if (typeof point.position === 'number' && Number.isFinite(point.position)) {
            lastPosition = point.position;
        } else {
            point.position = lastPosition;
        }

        const inPosition = Math.abs(point.position) > 0;
        point.active_reference_price = inPosition
            ? (point.exit_reference_price ?? point.entry_reference_price ?? null)
            : (point.entry_reference_price ?? point.exit_reference_price ?? null);
        point.active_reference_kind = inPosition ? 'exit' : 'entry';

        if (point.market_price == null) {
            point.market_price = point.active_reference_price ?? null;
        }
        point.decision = typeof point.decision === 'number' ? point.decision : 0;
        point.entryMarker = point.entryMarker ?? null;
        point.exitMarker = point.exitMarker ?? null;
        point.flipMarker = point.flipMarker ?? null;
    }

    return merged;
}

const CustomTooltip = memo(({ active, payload, label, entrySeriesLabel, exitSeriesLabel, activeReferenceColor }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload;
    const formattedDate = getDateAggregationKey(label, 'datetime');

    return (
        <div style={{
            background: 'rgba(30, 30, 30, 0.95)',
            border: '1px solid var(--surface-glass-border)',
            borderRadius: '10px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            padding: '12px',
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
            pointerEvents: 'none'
        }}>
            <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontWeight: '600', fontSize: '0.75rem' }}>
                {formattedDate}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {payload.map((entry: any, index: number) => {
                    const allowedKeys = ['market_price', 'active_reference_price', 'position', 'entryMarker', 'exitMarker', 'flipMarker'];
                    if (!allowedKeys.includes(entry.dataKey)) return null;

                    let displayName = entry.name;
                    let displayValue = formatNumber(entry.value, 4);
                    let color = entry.color || 'white';

                    if (entry.dataKey === 'market_price') {
                        displayName = 'Market Price';
                    } else if (entry.dataKey === 'active_reference_price') {
                        displayName = data.active_reference_kind === 'exit' ? exitSeriesLabel : entrySeriesLabel;
                        color = activeReferenceColor;
                    } else if (entry.dataKey === 'position') {
                        displayName = 'Position';
                        color = 'var(--accent-warning)';
                        displayValue = formatNumber(entry.value, 0);
                    } else if (entry.dataKey === 'entryMarker') {
                        displayName = 'Entry';
                        color = 'var(--accent-success)';
                        displayValue = formatNumber(data.entry_price ?? entry.value, 4);
                    } else if (entry.dataKey === 'exitMarker') {
                        displayName = 'Exit';
                        color = 'var(--accent-danger)';
                        displayValue = formatNumber(data.exit_price ?? entry.value, 4);
                    } else if (entry.dataKey === 'flipMarker') {
                        displayName = 'Flip';
                        color = 'var(--accent-warning)';
                    }

                    if (entry.value === null || entry.value === undefined) return null;

                    return (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                            <span style={{ opacity: 0.8 }}>{displayName}:</span>
                            <span style={{ color, fontWeight: 'bold' }}>{displayValue}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export const QuantArcExecutionWidget = memo(({ data, chartData, chartSeriesLabels }: Props) => {
    const series = useMemo(() => {
        const merged = mergeExecutionSeries(data, chartData);
        if (merged.length > 600) {
            return downsampleWithMarkers(merged, 500, 'market_price', ['entryMarker', 'exitMarker', 'flipMarker']);
        }
        return merged;
    }, [data, chartData]);

    const entrySeriesLabel = chartSeriesLabels?.entry_reference_price ?? 'Entry Reference';
    const exitSeriesLabel = chartSeriesLabels?.exit_reference_price ?? 'Exit Reference';
    const showReferenceLine = Boolean(chartData?.length);
    const activeReferenceColor = '#60a5fa';

    if (!series.length) {
        return (
            <WidgetWrapper title="🎯 Execution & Position">
                <div style={{ color: 'var(--text-muted)', padding: '1rem' }}>No execution data available.</div>
            </WidgetWrapper>
        );
    }

    return (
        <WidgetWrapper title="🎯 Execution & Position">
            <div style={{ width: '100%', height: '100%', padding: '0.5rem 0' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorPosExec" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="var(--accent-warning)" stopOpacity={0.15}/>
                                <stop offset="95%" stopColor="var(--accent-warning)" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                        <XAxis 
                            dataKey="time" 
                            tickFormatter={(value) => getDateAggregationKey(value)} 
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }} 
                            axisLine={false}
                            tickLine={false}
                            minTickGap={30}
                        />
                        <YAxis 
                            yAxisId="price" 
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }} 
                            tickFormatter={(value) => formatNumber(value, 2)}
                            axisLine={false}
                            tickLine={false}
                            domain={['auto', 'auto']}
                        />
                        <YAxis 
                            yAxisId="position" 
                            orientation="right"
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }} 
                            allowDecimals={false}
                            axisLine={false}
                            tickLine={false}
                            domain={['auto', 'auto']}
                        />
                        <Tooltip
                            content={
                                <CustomTooltip 
                                    entrySeriesLabel={entrySeriesLabel} 
                                    exitSeriesLabel={exitSeriesLabel}
                                    activeReferenceColor={activeReferenceColor}
                                />
                            }
                            animationDuration={0}
                            cursor={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1 }}
                        />
                        
                        {/* Position Background */}
                        <Area
                            yAxisId="position"
                            type="stepAfter"
                            dataKey="position"
                            name="position"
                            stroke="none"
                            fillOpacity={1}
                            fill="url(#colorPosExec)"
                            isAnimationActive={false}
                        />

                        {/* Market Price Line */}
                        {showReferenceLine ? (
                            <Line
                                yAxisId="price"
                                type={series.length > 800 ? "linear" : "monotone"}
                                dataKey="active_reference_price"
                                name="active_reference_price"
                                stroke={activeReferenceColor}
                                strokeWidth={1.75}
                                dot={false}
                                isAnimationActive={false}
                                connectNulls
                            />
                        ) : (
                            <Line
                                yAxisId="price"
                                type={series.length > 800 ? "linear" : "monotone"}
                                dataKey="market_price"
                                name="market_price"
                                stroke="#8884d8"
                                strokeWidth={1}
                                dot={false}
                                isAnimationActive={false}
                            />
                        )}

                        {/* Trade Markers */}
                        <Scatter 
                            yAxisId="price" 
                            dataKey="entryMarker" 
                            name="entryMarker" 
                            fill="var(--accent-success)" 
                            isAnimationActive={false} 
                            shape="circle"
                        />
                        <Scatter 
                            yAxisId="price" 
                            dataKey="exitMarker" 
                            name="exitMarker" 
                            fill="var(--accent-danger)" 
                            isAnimationActive={false} 
                            shape="circle"
                        />
                        <Scatter 
                            yAxisId="price" 
                            dataKey="flipMarker" 
                            name="flipMarker" 
                            fill="var(--accent-warning)" 
                            isAnimationActive={false} 
                            shape="cross"
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </WidgetWrapper>
    );
});
